import React, { useState } from 'react'
import { Alert, Box, Button, Stack, Typography } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DownloadIcon from '@mui/icons-material/Download'

/**
 * The 10 one-time recovery codes, shown once (#1724): when the app is set up
 * and when new ones are made. Only their hashes are kept, so this is the only
 * time anyone sees them.
 */
const RecoveryCodesList: React.FC<{ codes: string[], account?: string }> = ({ codes, account }) => {
  const [copied, setCopied] = useState(false)
  const text = codes.join('\n')

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  const download = () => {
    const blob = new Blob([`Platinum recovery codes${account ? ` for ${account}` : ''}\nEach code works once.\n\n${text}\n`], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'platinum-recovery-codes.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Stack spacing={1.5}>
      <Alert severity="warning">
        Save these recovery codes somewhere safe. Each one works once, in place of a code from your app, if you lose your phone. You will not see them again.
      </Alert>
      <Box
        component="ol"
        aria-label="Recovery codes"
        sx={{
          m: 0, p: 2, pl: 5, borderRadius: 1, border: '1px solid', borderColor: 'divider',
          fontFamily: 'monospace', fontSize: '1rem', columns: 2, columnGap: 4,
        }}
      >
        {codes.map((c) => <li key={c}>{c}</li>)}
      </Box>
      <Stack direction="row" spacing={1}>
        <Button size="small" color="info" variant="outlined" startIcon={<ContentCopyIcon />} onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button size="small" color="info" variant="outlined" startIcon={<DownloadIcon />} onClick={download}>
          Download
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Lost your phone and your codes? Ask a Manager to reset your two-step login.
      </Typography>
    </Stack>
  )
}

export default RecoveryCodesList
